import React, { Component } from 'react';

/**
 * InteractiveGrid
 * Canvas-based dot grid with mouse-reactive spotlight, magnetic pull, and connecting lines.
 * Listens on window for mousemove so overlapping z-index layers don't block interaction.
 */
class InteractiveGrid extends Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.wrapRef = React.createRef();
        this.animFrame = null;
        this.dots = [];
        this.mouse = { x: -9999, y: -9999 };
        this.mouseTarget = { x: -9999, y: -9999 };
        this.isMouseOver = false;
        this.canvasRect = null;

        // Config — tune these to taste
        this.SPACING = 42;
        this.DOT_RADIUS = 1.2;
        this.INFLUENCE_RADIUS = 180;
        this.PULL_STRENGTH = 20;
        this.LINE_DISTANCE = 55;
        this.BASE_ALPHA = 0.07;
        this.MAX_ALPHA = 0.6;
    }

    componentDidMount() {
        this.initCanvas();
        this.createDots();
        this.startLoop();

        // Listen on window so z-index layers above canvas don't block mouse
        window.addEventListener('mousemove', this.handleGlobalMouseMove);
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('mousemove', this.handleGlobalMouseMove);
        window.removeEventListener('resize', this.handleResize);
        if (this.animFrame) cancelAnimationFrame(this.animFrame);
    }

    // ══════════════════════════════════════
    //  CANVAS SETUP
    // ══════════════════════════════════════

    initCanvas = () => {
        const canvas = this.canvasRef.current;
        const wrap = this.wrapRef.current;
        if (!canvas || !wrap) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = wrap.offsetWidth;
        const h = wrap.offsetHeight;

        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';

        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        this.ctx = ctx;
        this.w = w;
        this.h = h;

        // Cache bounding rect for mouse coordinate conversion
        this.canvasRect = canvas.getBoundingClientRect();
    };

    handleResize = () => {
        this.initCanvas();
        this.createDots();
    };

    // ══════════════════════════════════════
    //  DOT GRID
    // ══════════════════════════════════════

    createDots = () => {
        this.dots = [];
        const cols = Math.ceil(this.w / this.SPACING) + 2;
        const rows = Math.ceil(this.h / this.SPACING) + 2;
        const offsetX = (this.w - (cols - 1) * this.SPACING) / 2;
        const offsetY = (this.h - (rows - 1) * this.SPACING) / 2;

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const x = offsetX + c * this.SPACING;
                const y = offsetY + r * this.SPACING;
                this.dots.push({ ox: x, oy: y, x, y });
            }
        }
    };

    // ══════════════════════════════════════
    //  MOUSE — global listener
    // ══════════════════════════════════════

    handleGlobalMouseMove = (e) => {
        // Re-measure rect periodically (scroll, layout shifts)
        if (!this.canvasRect || this._rectAge > 60) {
            const canvas = this.canvasRef.current;
            if (canvas) this.canvasRect = canvas.getBoundingClientRect();
            this._rectAge = 0;
        }
        this._rectAge = (this._rectAge || 0) + 1;

        if (!this.canvasRect) return;

        const rect = this.canvasRect;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if mouse is within the canvas area (with some padding)
        const pad = 50;
        if (x >= -pad && x <= this.w + pad && y >= -pad && y <= this.h + pad) {
            this.mouseTarget.x = x;
            this.mouseTarget.y = y;
            this.isMouseOver = true;
        } else {
            this.isMouseOver = false;
            this.mouseTarget.x = -9999;
            this.mouseTarget.y = -9999;
        }
    };

    // ══════════════════════════════════════
    //  ANIMATION LOOP
    // ══════════════════════════════════════

    startLoop = () => {
        const tick = () => {
            this.update();
            this.drawFrame();
            this.animFrame = requestAnimationFrame(tick);
        };
        this.animFrame = requestAnimationFrame(tick);
    };

    update = () => {
        // Smooth mouse lerp
        const lerp = this.isMouseOver ? 0.1 : 0.06;
        this.mouse.x += (this.mouseTarget.x - this.mouse.x) * lerp;
        this.mouse.y += (this.mouseTarget.y - this.mouse.y) * lerp;

        const mx = this.mouse.x;
        const my = this.mouse.y;
        const radius = this.INFLUENCE_RADIUS;
        const pull = this.PULL_STRENGTH;

        for (let i = 0; i < this.dots.length; i++) {
            const dot = this.dots[i];
            const dx = mx - dot.ox;
            const dy = my - dot.oy;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < radius && this.isMouseOver) {
                const factor = 1 - dist / radius;
                const eased = factor * factor;
                dot.x = dot.ox + (dx / dist) * eased * pull;
                dot.y = dot.oy + (dy / dist) * eased * pull;
            } else {
                // Spring back
                dot.x += (dot.ox - dot.x) * 0.08;
                dot.y += (dot.oy - dot.y) * 0.08;
            }
        }
    };

    // ══════════════════════════════════════
    //  DRAW — separated from React render
    // ══════════════════════════════════════

    drawFrame = () => {
        const ctx = this.ctx;
        if (!ctx) return;

        ctx.clearRect(0, 0, this.w, this.h);

        const mx = this.mouse.x;
        const my = this.mouse.y;
        const radius = this.INFLUENCE_RADIUS;

        // ── Connecting lines near cursor ──
        if (this.isMouseOver) {
            for (let i = 0; i < this.dots.length; i++) {
                const a = this.dots[i];
                const distA = Math.hypot(mx - a.x, my - a.y);
                if (distA > radius * 1.3) continue;

                for (let j = i + 1; j < this.dots.length; j++) {
                    const b = this.dots[j];
                    const dotDist = Math.hypot(a.x - b.x, a.y - b.y);
                    if (dotDist > this.LINE_DISTANCE) continue;

                    const distB = Math.hypot(mx - b.x, my - b.y);
                    const avgDist = (distA + distB) / 2;

                    if (avgDist < radius) {
                        const factor = 1 - avgDist / radius;
                        const alpha = factor * factor * 0.3;

                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
                        ctx.lineWidth = 0.7;
                        ctx.stroke();
                    }
                }
            }
        }

        // ── Draw dots ──
        for (let i = 0; i < this.dots.length; i++) {
            const dot = this.dots[i];
            const dist = Math.hypot(mx - dot.x, my - dot.y);

            let alpha = this.BASE_ALPHA;
            let r = this.DOT_RADIUS;

            if (dist < radius && this.isMouseOver) {
                const factor = 1 - dist / radius;
                const eased = factor * factor;
                alpha = this.BASE_ALPHA + (this.MAX_ALPHA - this.BASE_ALPHA) * eased;
                r = this.DOT_RADIUS + 2 * eased;
            }

            // Dot
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, r, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.fill();

            // Teal glow on close dots
            if (dist < radius * 0.45 && this.isMouseOver) {
                const gf = 1 - dist / (radius * 0.45);
                const glowEased = gf * gf;
                ctx.beginPath();
                ctx.arc(dot.x, dot.y, r + 5 * glowEased, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(20, 184, 166, ${glowEased * 0.18})`;
                ctx.fill();
            }
        }

        // ── Ambient spotlight ──
        if (this.isMouseOver) {
            const grad = ctx.createRadialGradient(mx, my, 0, mx, my, radius * 1.2);
            grad.addColorStop(0, 'rgba(20, 184, 166, 0.07)');
            grad.addColorStop(0.4, 'rgba(12, 141, 162, 0.03)');
            grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, this.w, this.h);
        }
    };

    // ══════════════════════════════════════
    //  REACT RENDER
    // ══════════════════════════════════════

    render() {
        return (
            <div ref={this.wrapRef} className="mzb-hero__grid-canvas-inner">
                <canvas
                    ref={this.canvasRef}
                    className="mzb-hero__interactive-canvas"
                />
            </div>
        );
    }
}

export default InteractiveGrid;